// ZZYON LEX — parser CSV + XLSX no browser.
// Replica a lógica de themis/scripts/import_processos.py em JS:
//   - chaves normalizadas (snake_case, sem acento)
//   - aliases: numero_cnj/cnj/processo · cnpj_icaro/cnpj
//   - valor BR (1.234,56) ou US (1234.56)
//   - data ISO ou DD/MM/AAAA
//   - inferência tribunal/tipo/área a partir do CNJ

import { lex } from './lex-client.js';
import { ICARO_CNPJS, filialDe } from './cnpjs-icaro.js';
import { parseCnj, tribunalDe, tipoDe, areaDe } from './cnj.js';

// ─── Loaders ───────────────────────────────────────────────────────
export async function carregarCsv(file) {
  const text = await file.text();
  return parseCsvText(text);
}

function parseCsvText(text) {
  // Parser simples — respeita aspas duplas e não suporta escape com \"\"\".
  // Suficiente para os templates fornecidos.
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length < 1) return [];
  const headers = splitCsvLine(lines[0]);
  return lines.slice(1).map(line => {
    const cols = splitCsvLine(line);
    const row = {};
    headers.forEach((h, i) => { row[h] = cols[i] ?? ''; });
    return row;
  });
}

function splitCsvLine(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { inQuotes = !inQuotes; continue; }
    if (c === ',' && !inQuotes) { out.push(cur); cur = ''; continue; }
    cur += c;
  }
  out.push(cur);
  return out.map(s => s.trim());
}

export async function carregarXlsx(file) {
  // Carrega xlsx dinamicamente via CDN para não inflar o bundle inicial.
  if (!window.XLSX) {
    await loadScript('https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js');
  }
  const buffer = await file.arrayBuffer();
  const wb = window.XLSX.read(buffer, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  return window.XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Falha ao carregar ' + src));
    document.head.appendChild(s);
  });
}

// ─── Normalização ──────────────────────────────────────────────────
const normKey = (k) =>
  String(k || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

function digits(v) {
  if (v == null) return '';
  return String(v).replace(/\D/g, '');
}

function toFloat(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v;
  let s = String(v).trim().replace(/R\$\s?/i, '').replace(/\s/g, '');
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function toIsoDate(v) {
  if (v == null || v === '') return null;
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return null;
}

const STATUS_VALIDOS = new Set([
  'em_andamento', 'suspenso', 'arquivado_provisorio',
  'transitado_julgado', 'acordo', 'extinto', 'arquivado_definitivo',
]);
const POLO_VALIDO = new Set(['autor', 'reu', 'terceiro']);
const RISCO_VALIDO = new Set(['provavel', 'possivel', 'remoto', 'sem_classificacao']);

// ─── Validação por linha ───────────────────────────────────────────
export function normalizarLinha(rawIn) {
  const raw = {};
  for (const [k, v] of Object.entries(rawIn)) raw[normKey(k)] = v;

  const erros = [];

  const cnjRaw = raw.numero_cnj || raw.cnj || raw.processo || raw.numero;
  const parsed = parseCnj(String(cnjRaw || ''));
  if (!parsed) {
    return { ok: false, erros: [`CNJ inválido ou ausente: "${cnjRaw || ''}"`], raw };
  }

  const cnpjRaw = raw.cnpj_icaro || raw.cnpj || raw.filial_cnpj;
  const cnpj = digits(cnpjRaw);
  if (!ICARO_CNPJS[cnpj]) {
    return {
      ok: false,
      erros: [`CNPJ "${cnpjRaw || ''}" não é uma filial Ícaro válida`],
      raw,
    };
  }

  let polo = String(raw.polo || 'reu').toLowerCase().trim();
  if (!POLO_VALIDO.has(polo)) polo = 'reu';

  let status = String(raw.status || 'em_andamento').toLowerCase().trim();
  if (!STATUS_VALIDOS.has(status)) status = 'em_andamento';

  let risco = String(raw.risco || '').toLowerCase().trim() || null;
  if (risco && !RISCO_VALIDO.has(risco)) risco = 'sem_classificacao';

  const tribunal = raw.tribunal || tribunalDe(parsed);
  const tipo = raw.tipo || tipoDe(parsed) || 'civel';
  const classe = raw.classe_processual || raw.classe;
  const area = (raw.area_codigo || '').trim().toUpperCase() || areaDe(parsed, classe);

  const filial = raw.filial_codigo || filialDe(cnpj) || null;

  const payload = {
    numero_cnj: parsed.numeroCompleto,
    area_codigo: area,
    tipo,
    polo,
    tribunal,
    vara: raw.vara || null,
    comarca: raw.comarca || null,
    uf: raw.uf || null,
    cnpj_icaro: cnpj,
    filial_codigo: filial,
    classe_processual: classe || null,
    assunto: raw.assunto || null,
    contraparte: raw.contraparte || raw.autor || raw.reu || null,
    contraparte_documento: digits(raw.contraparte_doc || raw.contraparte_documento) || null,
    advogado_responsavel: raw.advogado || raw.advogado_responsavel || null,
    valor_causa: toFloat(raw.valor_causa),
    valor_provisao: toFloat(raw.valor_provisao),
    risco,
    status,
    data_distribuicao: toIsoDate(raw.data_distribuicao),
    ultima_movimentacao_data: toIsoDate(raw.ultima_movimentacao_data),
    ultima_movimentacao_descricao: raw.ultima_movimentacao_descricao || null,
    fonte_dados: raw.fonte_dados || 'manual',
  };

  return { ok: erros.length === 0, payload, erros, raw };
}

// ─── Persistência ──────────────────────────────────────────────────
export async function importarProcessos(rows) {
  let inseridos = 0, atualizados = 0;
  const errosDb = [];

  for (const r of rows) {
    if (!r.ok) continue;
    try {
      const existe = await lex
        .from('processos')
        .select('id')
        .eq('numero_cnj', r.payload.numero_cnj)
        .maybeSingle();

      if (existe.data?.id) {
        const { error } = await lex
          .from('processos')
          .update(r.payload)
          .eq('id', existe.data.id);
        if (error) throw error;
        atualizados++;
      } else {
        const { error } = await lex.from('processos').insert(r.payload);
        if (error) throw error;
        inseridos++;
      }
    } catch (e) {
      errosDb.push(`${r.payload.numero_cnj}: ${e.message || e}`);
    }
  }

  return { inseridos, atualizados, erros_db: errosDb };
}
